/**
 * A tenant rule's trigger as the editor and the pre-fill see it. An On State Transition
 * trigger carries its scope as a LIST of transitions (C18): the rule runs on every one of
 * them and the backend materializes one rule_transitions row per entry. `fromState`/`toState`
 * are the pre-C18 single pair — still read (as a one-element scope) so rules saved before the
 * list existed keep their mapping; never written any more.
 */
export interface TransitionRef {
  from: string;
  to: string;
  /**
   * The transition key ("DRAFT_CONFIRMED"), unique across all of the entity's state machines.
   * Written by the editor since an entity may carry several machines and two of them may share
   * a state pair; a ref stored before then has none and is matched by its pair.
   */
  key?: string;
  /** The state machine the transition belongs to (the entity field it drives), when known. */
  stateMachine?: string;
}

export interface TriggerValue {
  kind: string;
  transitions?: TransitionRef[];
  /**
   * The state machine an OnTransition scope belongs to (Multiple-State-Machines-Per-Entity.md
   * §3.4): the entity field the machine drives. A scope spans ONE machine; the backend refuses
   * one that names transitions of two. Absent on a rule stored before machines were named.
   */
  stateMachine?: string;
  /**
   * C18 step 3: the commands an On Command rule runs on, by DSL command name. It is the whole
   * scope — there is no wildcard, so a rule meant to run broadly enumerates the entity's
   * command set and every surface renders that list. Before C18 an On Command rule ran on
   * every command invocation, which is why "every command" used to be the only thing the
   * runs-on text could say.
   */
  commands?: string[];
  /**
   * C19: the fields an On field change rule guards. Read-only here — the rule's guard is code
   * generated from its DSL declaration, so this surface renders the reach rather than edits it.
   */
  fields?: string[];
  fromState?: string;
  toState?: string;
  action?: string;
}

export const ON_TRANSITION = 'OnTransition';
export const ON_COMMAND = 'OnCommand';
/**
 * C19. Not offered as a choice: the enforcement is a save-boundary guard generated from the
 * rule's DSL declaration, so a tenant cannot author one. It stays in the vocabulary so a stored
 * row renders its own kind and its guarded fields instead of an empty select — the same reason
 * the inert kinds stay.
 */
export const ON_FIELD_CHANGE = 'OnFieldChange';

export function isOnFieldChange(trigger: TriggerValue | null | undefined): boolean {
  return trigger?.kind?.toLowerCase() === ON_FIELD_CHANGE.toLowerCase();
}

/**
 * A row invariant over the record's own fields, enforced by the same generated save-boundary guard
 * as {@link ON_FIELD_CHANGE} and, for the same reason, not offered as a choice.
 */
export const ON_SAVE = 'OnSave';

export function isOnSave(trigger: TriggerValue | null | undefined): boolean {
  return trigger?.kind?.toLowerCase() === ON_SAVE.toLowerCase();
}

/** The trigger a JSONB control holds: a JSON string, an object, or nothing. Null when unreadable. */
export function parseTrigger(data: unknown): TriggerValue | null {
  const record = asRecord(data);
  if (!record) return null;
  const kind = readField(record, 'kind');
  if (!kind) return null;
  return {
    kind,
    transitions: readTransitions(pick(record, 'transitions')),
    stateMachine: readField(record, 'stateMachine'),
    commands: readCommands(pick(record, 'commands')),
    fields: readCommands(pick(record, 'fields')),
    fromState: readField(record, 'fromState'),
    toState: readField(record, 'toState'),
    action: readField(record, 'action'),
  };
}

/** The object behind a control's value, whether it holds JSON text or an object already. */
function asRecord(data: unknown): Record<string, unknown> | null {
  if (data === null || data === undefined || data === '') return null;
  let value: unknown = data;
  if (typeof data === 'string') {
    try {
      value = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** A trigger arrives in either casing: the editor writes camelCase, the backend DTO PascalCase. */
function pick(record: Record<string, unknown>, name: string): unknown {
  return record[name] ?? record[name.charAt(0).toUpperCase() + name.slice(1)];
}

function readField(record: Record<string, unknown>, name: string): string | undefined {
  return readString(pick(record, name));
}

export function isOnTransition(trigger: TriggerValue | null | undefined): boolean {
  return trigger?.kind?.toLowerCase() === ON_TRANSITION.toLowerCase();
}

export function isOnCommand(trigger: TriggerValue | null | undefined): boolean {
  return trigger?.kind?.toLowerCase() === ON_COMMAND.toLowerCase();
}

/** The commands the trigger names, trimmed and de-duplicated case-insensitively. */
export function effectiveCommands(trigger: TriggerValue | null | undefined): string[] {
  const seen = new Set<string>();
  return (trigger?.commands ?? [])
    .map((c) => c?.trim())
    .filter((c): c is string => !!c)
    .filter((c) => {
      const key = c.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function onCommandTrigger(commands: string[]): TriggerValue {
  return { kind: ON_COMMAND, commands };
}

/** The transitions the trigger names: the list when declared, else the legacy pair, else none. */
export function effectiveTransitions(trigger: TriggerValue | null | undefined): TransitionRef[] {
  if (!trigger) return [];
  if (trigger.transitions) {
    return trigger.transitions.filter((t) => t.from && t.to);
  }
  if (trigger.fromState && trigger.toState) {
    return [{ from: trigger.fromState, to: trigger.toState }];
  }
  return [];
}

/**
 * An OnTransition trigger over the given refs. The scope's machine is the one every ref names
 * (the editor only offers one machine's transitions at a time); refs without a machine — a rule
 * stored before machines were named — leave it unset and the backend resolves by pair.
 */
export function onTransitionTrigger(transitions: TransitionRef[], stateMachine?: string): TriggerValue {
  const machines = new Set(transitions.map((t) => t.stateMachine).filter((m): m is string => !!m));
  stateMachine ??= machines.size === 1 ? [...machines][0] : undefined;
  return {
    kind: ON_TRANSITION,
    transitions: transitions.map(({ from, to, key }) => (key ? { from, to, key } : { from, to })),
    ...(stateMachine ? { stateMachine } : {}),
  };
}

/**
 * The declared transition a stored ref stands for: by key when the ref carries one (the exact
 * identity), else by its state pair — the first declared match, which is all a ref stored
 * before keys existed can say.
 */
export function resolveDeclared(declared: readonly TransitionRef[], ref: TransitionRef): TransitionRef | undefined {
  if (ref.key) {
    const byKey = declared.find((d) => d.key?.toUpperCase() === ref.key!.toUpperCase());
    if (byKey) return byKey;
  }
  return declared.find((d) => samePair(d, ref));
}

function samePair(a: TransitionRef, b: TransitionRef): boolean {
  return a.from.toUpperCase() === b.from.toUpperCase() && a.to.toUpperCase() === b.to.toUpperCase();
}

/** What the JSONB control stores. Only the list form is written — the legacy pair is retired. */
export function serializeTrigger(trigger: TriggerValue): string {
  if (isOnTransition(trigger)) {
    return JSON.stringify(onTransitionTrigger(effectiveTransitions(trigger), trigger.stateMachine));
  }
  if (isOnCommand(trigger)) {
    return JSON.stringify(onCommandTrigger(effectiveCommands(trigger)));
  }
  const { kind, action } = trigger;
  return JSON.stringify(action ? { kind, action } : { kind });
}

/**
 * Stable identity of a transition inside a select: its key when it has one (unique across the
 * entity's machines), else "FROM→TO" — upper-cased because the backend resolves a scope entry
 * against the declared transitions case-insensitively. A pair stored as `draft`/`confirmed`
 * must select the same option as `DRAFT`/`CONFIRMED`, not read as one the entity does not
 * declare and get dropped on the next edit.
 */
export function transitionKey(t: TransitionRef): string {
  return t.key ? t.key.toUpperCase() : `${t.from.toUpperCase()}→${t.to.toUpperCase()}`;
}

export function describeTransition(t: TransitionRef): string {
  return `${t.from} → ${t.to}`;
}

function readCommands(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((c): c is string => typeof c === 'string' && c.length > 0);
}

function readTransitions(value: unknown): TransitionRef[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((t): t is Record<string, unknown> => typeof t === 'object' && t !== null)
    .map((t) => {
      const key = readString(t.key ?? t.Key);
      return { from: readString(t.from ?? t.From) ?? '', to: readString(t.to ?? t.To) ?? '', ...(key ? { key } : {}) };
    });
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
